import type { Spine } from '@esotericsoftware/spine-pixi-v8';

export type AnimationState = 'idle' | 'run' | 'jump' | 'walk' | 'shoot' | 'death' | 'aim';

export class SpineAnimationController {
	private spine: Spine;
	private currentState: AnimationState = 'idle';
	private previousState: AnimationState | null = null;
	private loop: boolean = true;
	private timeScale: number = 1;

	constructor(spine: Spine) {
		this.spine = spine;
		this.setAnimation('idle', true);
	}

	setAnimation(state: AnimationState, loop: boolean = true, force: boolean = false) {
		if (!force && this.currentState === state) return;

		const animationName = this.getAnimationName(state);
		if (!animationName) {
			console.warn(`Animation not found for state: ${state}`);
			return;
		}

		this.previousState = this.currentState;
		this.currentState = state;
		this.loop = loop;

		const track = this.spine.state.setAnimation(0, animationName, loop);
		if (track) {
			track.timeScale = this.timeScale;
		}
	}

	setTimeScale(scale: number) {
		this.timeScale = scale;
		const track = this.spine.state.tracks[0];
		if (track) {
			track.timeScale = scale;
		}
	}

	getAnimationName(state: AnimationState): string | null {
		const animationMap: Record<AnimationState, string> = {
			idle: 'idle',
			run: 'run',
			jump: 'jump',
			walk: 'walk',
			shoot: 'shoot',
			death: 'death',
			aim: 'aim'
		};

		const name = animationMap[state];
		if (!name) return null;

		// Check if animation exists in skeleton data
		const anim = this.spine.skeleton.data.findAnimation(name);
		return anim ? name : null;
	}

	getCurrentState(): AnimationState {
		return this.currentState;
	}

	update(delta: number) {
		// When Spine.autoUpdate=false, caller must update manually.
		this.spine.update(delta);
	}

	setFlipX(flip: boolean) {
		this.spine.scale.x = flip ? -Math.abs(this.spine.scale.x) : Math.abs(this.spine.scale.x);
	}

	setFlipY(flip: boolean) {
		this.spine.scale.y = flip ? -Math.abs(this.spine.scale.y) : Math.abs(this.spine.scale.y);
	}
}
